import { useCallback } from 'react'
import { CreditCard } from 'lucide-react'
import { useStripePortalSession } from '../../hooks/useStripePortalSession'
import { Button } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'

interface ManagePaymentMethodButtonProps {
  /** Where the button is being used, for analytics */
  from?: string
  /** Custom class name */
  className?: string
  /** Button variant */
  variant?: 'outline' | 'ghost' | 'primary'
  /** Button size */
  size?: 'sm' | 'md' | 'xl'
  /** Show icon */
  showIcon?: boolean
  /** Custom button text */
  children?: React.ReactNode
}

/**
 * Reusable button component that opens the Stripe portal for managing payment methods
 */
export const ManagePaymentMethodButton: React.FC<ManagePaymentMethodButtonProps> = ({
  from = 'unknown',
  className,
  variant = 'outline',
  size = 'xl',
  showIcon = false,
  children,
}) => {
  const { mutate: openStripePortal, isPending } = useStripePortalSession()

  const handleClick = useCallback(() => {
    window.ampltd.trackEvent('Billing', {
      action: 'manage payment method clicked',
      from,
    })
    openStripePortal()
  }, [openStripePortal, from])

  return (
    <Button
      size={size}
      variant={variant}
      onClick={handleClick}
      disabled={isPending}
      className={twMerge('w-full', className)}
    >
      {showIcon && <CreditCard className="mr-2 h-4 w-4" />}
      {isPending ? 'Opening...' : children || 'Manage Payment Method'}
    </Button>
  )
}
